import { style } from '@vanilla-extract/css';
import { vars } from '@/shared/ui/theme/contract.css';

/**
 * 섭외 단계 줄 — 탭 바로 아래, 그 단계 내용 바로 위에 선다(2026-09-28).
 *
 * 세 단계가 같은 자리에서 같은 줄을 본다. 예전에는 발송 확인 패널 안에만 있어 앞 두 단계에서는
 * 자기 위치를 알 수 없었다.
 */
export const crumbs = style({
  display: 'flex',
  alignItems: 'center',
  gap: vars.spacing.gap.sm,
  margin: 0,
  padding: 0,
  listStyle: 'none',
  fontSize: vars.typography.fontSize.xs,
  color: vars.typography.textColor.secondary,
});

/** 지금 단계 — 굵기와 밝기로만 가른다. 색 하나로는 흑백 화면에서 안 보인다. */
export const now = style({
  color: vars.typography.textColor.primary,
  fontWeight: String(vars.typography.fontWeight.bold),
});

/**
 * 「NZ 8회차」 — 줄 맨 오른쪽(2026-09-29).
 *
 * 단계 목록(`crumbs`) 밖으로 나오면서 거기서 상속하던 글자 크기·색을 잃었다. 같은 값을 직접
 * 준다 — 단계 이름과 같은 눈금이되 지금 단계보다는 약하다. 보조 문구이지 단계가 아니다.
 */
export const round = style({
  marginLeft: 'auto',
  flexShrink: 0,
  fontSize: vars.typography.fontSize.xs,
  color: vars.typography.textColor.secondary,
  whiteSpace: 'nowrap',
});

export const arrow = style({ marginRight: vars.spacing.gap.xs });

/**
 * 지난 단계 — 눌러서 되돌아간다. 작성기·발송 확인의 머리줄을 지운 뒤로 이것이 유일한 길이다
 * (2026-09-28). 글자 크기와 색은 옆 단계와 같게 두고 밑줄로만 「누를 수 있다」 를 말한다.
 */
export const back = style({
  padding: 0,
  border: 0,
  background: 'none',
  font: 'inherit',
  color: 'inherit',
  textDecoration: 'underline',
  textUnderlineOffset: 2,
  cursor: 'pointer',
  ':hover': { color: vars.typography.textColor.primary },
});

/**
 * 단계 줄과 작업 중 표시를 한 줄에 둔다 — 표시는 오른쪽 끝(IA §3-7). 좁은 창에서는 표시가
 * 다음 줄로 내려간다. 줄바꿈은 미디어쿼리 밖에 상시로 둔다 — 덮개는 앱 안의 창이라 뷰포트
 * 폭과 실제 폭이 다르다.
 */
export const row = style({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: vars.spacing.gap.sm,
});

/** 사실만 알리는 한 줄 — 단계 줄과 같은 크기·색으로 둔다. 편집을 막는 경고가 아니다. */
export const presence = style({
  margin: 0,
  fontSize: vars.typography.fontSize.xs,
  color: vars.typography.textColor.secondary,
});

/** 단계 줄을 감싸는 `nav` — 폭을 다 쓰고 안의 `ol` 이 좌우로 벌어진다. */
export const nav = style({ display: 'flex', flexGrow: 1, minWidth: 0 });

/**
 * 이름만 주는 제목 — 화면에는 단계 이름이 이미 보인다.
 *
 * `VisuallyHidden` 아톰을 안 쓰는 이유: 그 아톰이 `id` 를 안 넘겨 `aria-labelledby` 로 가리킬
 * 수가 없다(이 레포에서 전에 걸린 함정).
 */
export const srOnly = style({
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
});

/**
 * 「US · 1,020명」 — 단계 목록 바로 뒤. 회차보다 앞이라 발송 축(나라·대상)이 먼저 읽힌다.
 *
 * 지금 단계와 같은 무게로 둔다(2026-09-30). 이 줄에서 가장 자주 확인하는 수라 보조 문구 색으로
 * 두면 단계 이름들 사이에 묻힌다.
 */
export const target = style({
  flexShrink: 0,
  fontSize: vars.typography.fontSize.xs,
  fontWeight: String(vars.typography.fontWeight.bold),
  color: vars.typography.textColor.primary,
  whiteSpace: 'nowrap',
});

/**
 * 「대상 수정」 — 수 바로 옆.
 *
 * 지난 단계 crumb 과 달리 **밑줄 없이 파란 글자**다(2026-09-30). 바로 앞의 굵은 수와 붙어 서는
 * 자리라 밑줄까지 있으면 두 조각이 한 덩어리로 뭉쳐 보인다. 색으로 「누를 수 있다」 를 말한다.
 *
 * hover·focus 에서는 **파랑에서 회색 글자로 내려온다**(2026-09-30). 흰색으로 올리는 것은 보기
 * 나빴고, 강조색을 다른 강조색으로 옮기면 뜻이 바뀌어 보인다. 회색 계열은 옆의 대상 수와 같은
 * 식구라 눌리는 동안만 한 덩어리로 붙는다.
 *
 * 키보드에는 hover 가 없어 `:focus-visible` 에도 같이 걸었다 — 밑줄이 없는 자리라 색이 유일한
 * 신호다. 색이 튀지 않게 `fast` 로 건너간다(전역 reduced-motion 이 0.01ms 로 끈다).
 */
export const editTargets = style({
  marginLeft: vars.spacing.gap.sm,
  padding: 0,
  border: 0,
  background: 'none',
  font: 'inherit',
  fontWeight: String(vars.typography.fontWeight.medium),
  color: vars.bright.status.info,
  cursor: 'pointer',
  transition: `color ${vars.animation.duration.fast} ${vars.animation.easing.easeOut}`,
  selectors: {
    '&:hover, &:focus-visible': {
      color: vars.typography.textColor.secondary,
    },
  },
});
